import { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import {
  Home, Swords, Trophy, Store, User, Bell, Search, Hash, Brain,
  Shield, Wallet as WalletIcon, Users, MessageCircle, Settings as Cog,
  LogOut, Plus, Gem, Package, Flag, PawPrint, Car,
} from 'lucide-react';
import { useAuth } from '../lib/store';
import { compact } from '../lib/api';
import { ARENA } from '../lib/classTheme';
import { Laurel, Torch } from './ArenaFrame';
import { ThemeToggle } from './ThemeSwitcher';
import useTheme from '../lib/useTheme';

const NAV = [
  { to: '/feed',      icon: Home,    label: 'Wall' },
  { to: '/groups',    icon: Hash,    label: 'Cities' },
  { to: '/arena',     icon: Swords,  label: 'Arena' },
  { to: '/race',      icon: Flag,    label: 'Circus' },
  { to: '/hero',      icon: User,    label: 'Hero' },
];

export default function Shell() {
  const { user, hero, unread, pendingGifts, logout, city } = useAuth();
  const { L, isNeon } = useTheme();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);

  return (
    <div className="min-h-screen">
      {/* ═══════ TOP BAR ═══════ */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/feed" className="flex items-center gap-2 shrink-0 group">
            <div className="relative w-9 h-9 rounded-xl grid place-items-center font-display
              font-black text-lg text-white transition-transform group-hover:scale-105">
              <a href="https://wof.im"><img src="/uploads/logo-wof.webp" alt="Wall of Freedom"/></a>
            </div>
            <span className="hidden sm:block font-display font-extrabold text-base tracking-wide">
              Wall of <span className="grad-text">Freedom</span>
            </span>
          </Link>

          <button onClick={() => nav('/groups')}
            className="hidden md:flex items-center gap-2 flex-1 max-w-xs rounded-xl px-3 py-2
              text-sm transition"
            style={{
              background: 'var(--stone-100)',
              color: 'var(--stone-400)',
              border: '1px solid var(--stone-200)',
            }}>
            <Search size={15} /> Search heroes, shops, cities…
          </button>

          <div className="flex-1" />

          <Link to="/wallet"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-sm text-white"
            style={{ background: ARENA.goldFrame, boxShadow: '0 3px 12px -3px rgba(201,151,58,.55)' }}>
            <Gem size={14} /> {compact(user?.points_balance || 0)}
          </Link>

          <Link to="/inventory" className="relative p-2 rounded-xl transition sm:hidden"
            style={{ color: 'var(--stone-600)' }}>
            <Package size={20} />
            {pendingGifts > 0 && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-mint-500 ring-2"
                style={{ '--tw-ring-color': 'var(--marble)' }} />
            )}
          </Link>

          <ThemeToggle />

          <Link to="/notifications" className="relative p-2 rounded-xl transition"
            style={{ color: 'var(--stone-600)' }}>
            <Bell size={20} />
            {unread > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[17px] h-[17px] px-1 rounded-full
                text-white text-[10px] font-black grid place-items-center"
                style={{ background: ARENA.blood }}>
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Link>

          <button onClick={() => setMenu(v => !v)} className="relative">
            <div className="w-[34px] h-[34px] rounded-full overflow-hidden"
              style={{ padding: 2, background: ARENA.goldFrame }}>
              <div className="w-full h-full rounded-full overflow-hidden grid place-items-center
                text-white font-black text-sm"
                style={{ background: 'linear-gradient(135deg,#dcb254,#a87a2b)' }}>
                {hero?.image_url || user?.avatar_url
                  ? <img src={hero?.image_url || user.avatar_url} alt=""
                      className="w-full h-full object-cover" />
                  : (user?.display_name || user?.username || '?')[0].toUpperCase()}
              </div>
            </div>
            {pendingGifts > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-mint-500 ring-2"
                style={{ '--tw-ring-color': 'var(--marble)' }} />
            )}
          </button>

          {menu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
              <div className="absolute right-4 top-14 z-50 w-64 card p-2 animate-fade-up">
                <div className="px-3 py-2.5 mb-1 rounded-xl"
                  style={{ background: 'var(--sand-100)', border: '1px solid var(--stone-200)' }}>
                  <div className="flex items-center gap-2">
                    <Laurel size={20} color={ARENA.gold} />
                    <div className="min-w-0">
                      <p className="font-display font-bold text-sm truncate">
                        {user?.display_name || user?.username}
                      </p>
                      <p className="text-[10px] font-semibold" style={{ color: 'var(--stone-500)' }}>
                        @{user?.username}{city && ` · ${city.handle}`}
                      </p>
                    </div>
                  </div>
                </div>

                <MenuLink to={`/u/${user?.username}`} icon={User} onClick={() => setMenu(false)}>
                  My profile
                </MenuLink>
                <MenuLink to="/pet" icon={PawPrint} onClick={() => setMenu(false)}>
                  Companion
                </MenuLink>
                <MenuLink to="/garage" icon={Car} onClick={() => setMenu(false)}>
                  Garage
                </MenuLink>
                <MenuLink to="/inventory" icon={Package} onClick={() => setMenu(false)}
                  badge={pendingGifts}>
                  Armory
                </MenuLink>
                <MenuLink to="/item-shop" icon={Store} onClick={() => setMenu(false)}>
                  Market
                </MenuLink>
                <MenuLink to="/wallet" icon={WalletIcon} onClick={() => setMenu(false)}>
                  Treasury
                </MenuLink>
                <MenuLink to="/referrals" icon={Users} onClick={() => setMenu(false)}>
                  Invite friends
                </MenuLink>
                <MenuLink to="/trivia" icon={Brain} onClick={() => setMenu(false)}>
                  Trivia
                </MenuLink>
                <MenuLink to="/guilds" icon={Shield} onClick={() => setMenu(false)}>
                  Legions
                </MenuLink>
                <MenuLink to="/leaderboard" icon={Trophy} onClick={() => setMenu(false)}>
                  Hall of Fame
                </MenuLink>
                <MenuLink to="/chat" icon={MessageCircle} onClick={() => setMenu(false)}>
                  Chats
                </MenuLink>

                {user?.role === 'producer' ? (
                  <MenuLink to="/shops?mine=1" icon={Store} onClick={() => setMenu(false)}>
                    My shops
                  </MenuLink>
                ) : (
                  <MenuLink to="/create-shop" icon={Plus} onClick={() => setMenu(false)}>
                    Open a shop
                  </MenuLink>
                )}

                {user?.role === 'admin' && (
                  <MenuLink to="/admin" icon={Shield} onClick={() => setMenu(false)}>
                    Senate (Admin)
                  </MenuLink>
                )}

                <MenuLink to="/settings" icon={Cog} onClick={() => setMenu(false)}>
                  Settings
                </MenuLink>

                <div className="divider !my-2" />
                <button onClick={() => { logout(); nav('/'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold
                    transition hover:bg-blood-500/10"
                  style={{ color: ARENA.blood }}>
                  <LogOut size={16} /> Log out
                </button>
              </div>
            </>
          )}
        </div>

        {/* gold under-line */}
        <div className="h-[2px]" style={{ background: ARENA.goldFrame, opacity: .55 }} />
      </header>

      {/* ═══════ BODY ═══════ */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 pb-24 lg:pb-6">
        <div className="lg:grid lg:grid-cols-[212px_1fr] lg:gap-6">
          <aside className="hidden lg:block">
            <nav className="sticky top-20 space-y-1">
              {NAV.map(n => <SideLink key={n.to} {...n} />)}
              <div className="divider" />
              <SideLink to="/pet"        icon={PawPrint} label="Companion" />
              <SideLink to="/garage"     icon={Car}      label="Garage" />
              <SideLink to="/inventory"  icon={Package}  label={L("armory")} badge={pendingGifts} />
              <SideLink to="/item-shop"  icon={Gem}      label={L("market")} />
              <div className="divider" />
              <SideLink to="/shops"       icon={Store}   label="Shops" />
              <SideLink to="/trivia"      icon={Brain}   label="Trivia" />
              <SideLink to="/leaderboard" icon={Trophy}  label={L("hallOfFame")} />
              <SideLink to="/guilds"      icon={Shield}  label={L("legions")} />
              <SideLink to="/referrals"   icon={Users}   label="Invite & earn" />
              <SideLink to="/chat"        icon={MessageCircle} label="Chats" />
            </nav>
          </aside>

          <main className="min-w-0"><Outlet /></main>
        </div>
      </div>

      {/* ═══════ MOBILE TABS ═══════ */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass safe-bottom"
        style={{ borderTop: '1px solid var(--stone-200)' }}>
        <div className="h-[2px]" style={{ background: ARENA.goldFrame, opacity: .5 }} />
        <div className="grid grid-cols-5 max-w-lg mx-auto">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) => `relative flex flex-col items-center gap-0.5 py-2 transition`}
              style={({ isActive }) => ({ color: isActive ? ARENA.goldD : 'var(--stone-400)' })}>
              {({ isActive }) => (
                <>
                  <div className="p-1.5 rounded-xl transition"
                    style={isActive ? {
                      background: 'rgba(220,178,84,.18)',
                      boxShadow: 'inset 0 0 0 1px rgba(220,178,84,.35)',
                    } : {}}>
                    <Icon size={19} strokeWidth={isActive ? 2.5 : 2} />
                  </div>
                  <span className="text-[9px] font-display font-bold tracking-wide">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function SideLink({ to, icon: Icon, label, badge }) {
  return (
    <NavLink to={to}
      className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[9px]
          font-black grid place-items-center" style={{ background: ARENA.blood }}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function MenuLink({ to, icon: Icon, children, onClick, badge }) {
  return (
    <Link to={to} onClick={onClick}
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition
        hover:bg-stone-100"
      style={{ color: 'var(--stone-700)' }}>
      <Icon size={16} style={{ color: 'var(--stone-400)' }} />
      <span className="flex-1">{children}</span>
      {badge > 0 && <span className="chip-mint">{badge}</span>}
    </Link>
  );
}

export function Avatar({ user, hero, size = 40, ring }) {
  const src = hero?.image_url || user?.avatar_url;
  const initial = (user?.display_name || user?.username || '?')[0].toUpperCase();
  return (
    <div className="relative rounded-full overflow-hidden grid place-items-center
      text-white font-black shrink-0"
      style={{
        width: size, height: size, fontSize: size * 0.4,
        padding: 2, background: ARENA.goldFrame,
        boxShadow: ring ? `0 0 0 2px ${ARENA.gold}` : undefined,
      }}>
      <div className="w-full h-full rounded-full overflow-hidden grid place-items-center"
        style={{ background: 'linear-gradient(135deg,#dcb254,#a87a2b)' }}>
        {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : initial}
      </div>
    </div>
  );
}